import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AllergiesData } from '@models/medical-history.model';
import { AlertService } from '@services/alert.service';
import { MedicalHistoryService } from '@services/medical-history.service';
import { MedpraxService } from '@services/medprax.service';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { NATURAL_INGREDIENTS } from 'src/app/constants';

@Component({
  selector: 'gc-allergies',
  templateUrl: './allergies.component.html',
  styleUrls: ['./allergies.component.scss'],
  standalone: false,
})
export class AllergiesComponent implements OnInit, OnChanges, OnDestroy {
  @Input() userId: number;
  @Input() allergiesData: AllergiesData;
  @Input() requestUuid: string | null = null;
  @Output() submittedStep = new EventEmitter<{ step: string; values: any }>();
  @ViewChild('input_chemical_allergy') inputFilter: ElementRef<HTMLInputElement>;

  subscriptions: Subscription[] = [];
  allergiesForm: FormGroup;
  drugClassesOptions = [];
  naturalIngredientsOptions = NATURAL_INGREDIENTS || [];
  medicineIngredientsOptions = [];
  initMedicineIngredientsOptions = [];
  newDrugAllergyForm = this.fb.group({
    allergy: this.fb.group({
      id: [''],
      code: [''],
      description: ['', Validators.required],
    }),
    code: [''],
    note: '',
  });
  newNaturalAllergyForm = this.fb.group({
    allergy: this.fb.group({
      code: [''],
      description: ['', Validators.required],
    }),
    code: [''],
    note: '',
  });
  newChemicalAllergyForm = this.fb.group({
    allergy: this.fb.group({
      id: [''],
      code: [''],
      classcode: [''],
      description: ['', Validators.required],
    }),
    code: [''],
    note: '',
  });

  constructor(
    private readonly fb: FormBuilder,
    private readonly medicalHistoryService: MedicalHistoryService,
    private readonly alertService: AlertService,
    private readonly medpraxService: MedpraxService,
  ) {}

  ngOnInit(): void {
    this.buildAllergiesForm();
    this.buildSelectLists();
    this.subscriptions.push(this.allergiesForm.get('no_allergies').valueChanges.subscribe((value) => this.disableAllergies(value)));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['allergiesData'] && !changes['allergiesData'].firstChange) {
      this.buildAllergiesForm();
    }
  }

  buildSelectLists() {
    this.medpraxService
      .getDrugClasses$()
      .pipe(take(1))
      .subscribe((drugClasses) => (this.drugClassesOptions = drugClasses));
    this.medpraxService
      .getMedicineIngredients$()
      .pipe(take(1))
      .subscribe((medicineIngredients) => {
        this.initMedicineIngredientsOptions = medicineIngredients;
        this.medicineIngredientsOptions = this.initMedicineIngredientsOptions;
      });
  }

  filterChemicalAllery() {
    const filterValue = this.inputFilter.nativeElement.value.toLowerCase();
    this.medicineIngredientsOptions = this.initMedicineIngredientsOptions.filter((option) =>
      option.description.toLowerCase().includes(filterValue),
    );
  }

  buildAllergiesForm() {
    const defaultAllergiesData = {
      no_allergies: 'false',
      warning: { urgent: false, message: '' },
      drug_classes: [],
      natural_ingredients: [],
      chemical_ingredients: [],
    };

    const noAllergies = (this.allergiesData?.no_allergies || defaultAllergiesData.no_allergies) === 'true' ? 'false' : 'true';
    this.allergiesForm = this.fb.group({
      no_allergies: [noAllergies, [Validators.required]],
      warning: [this.allergiesData?.warning?.message || ''],
      urgent: [this.allergiesData?.warning?.urgent || false],
      drug_classes:
        this.fb.array((this.allergiesData?.drug_classes || defaultAllergiesData.drug_classes)?.map((allergy) => this.fb.group(allergy))) ||
        this.fb.array([]),
      natural_ingredients:
        this.fb.array(
          (this.allergiesData?.natural_ingredients || defaultAllergiesData.natural_ingredients)?.map((allergy) => this.fb.group(allergy)),
        ) || this.fb.array([]),
      chemical_ingredients:
        this.fb.array(
          (this.allergiesData?.chemical_ingredients || defaultAllergiesData.chemical_ingredients)?.map((allergy) => this.fb.group(allergy)),
        ) || this.fb.array([]),
    });
    this.disableAllergies(noAllergies);
  }

  disableAllergies(value: string) {
    // Disable the form if they have no allergies

    if (value === 'false') {
      this.allergiesForm.disable({ emitEvent: false });
      this.allergiesForm.get('no_allergies').enable({ emitEvent: false });
      this.newDrugAllergyForm.disable({ emitEvent: false });
      this.newDrugAllergyForm.reset();
      this.newNaturalAllergyForm.disable({ emitEvent: false });
      this.newNaturalAllergyForm.reset();
      this.newChemicalAllergyForm.disable({ emitEvent: false });
      this.newChemicalAllergyForm.reset();
      this.allergiesForm.get('warning').setValue('');
      this.allergiesForm.get('urgent').setValue(false);
      this.allergiesForm.get('drug_classes').value.forEach((_, i) => this.remove('drug_classes', i));
      this.allergiesForm.get('natural_ingredients').value.forEach((_, i) => this.remove('natural_ingredients', i));
      this.allergiesForm.get('chemical_ingredients').value.forEach((_, i) => this.remove('chemical_ingredients', i));
    } else {
      this.allergiesForm.enable({ emitEvent: false });
      this.newDrugAllergyForm.enable({ emitEvent: false });
      this.newNaturalAllergyForm.enable({ emitEvent: false });
      this.newChemicalAllergyForm.enable({ emitEvent: false });
    }
  }

  addAllergyItem(type: 'drug_classes' | 'natural_ingredients' | 'chemical_ingredients') {
    let allergieItem = this.newDrugAllergyForm.value;
    switch (type) {
      case 'drug_classes':
        allergieItem = this.newDrugAllergyForm.value;
        this.newDrugAllergyForm.reset();
        break;
      case 'natural_ingredients':
        allergieItem = this.newNaturalAllergyForm.value;
        this.newNaturalAllergyForm.reset();
        break;
      case 'chemical_ingredients':
        allergieItem = this.newChemicalAllergyForm.value;
        this.newChemicalAllergyForm.reset();
        break;
    }
    const newAllergyForm = this.fb.group({
      description: [allergieItem.allergy.description, Validators.required],
      code: [allergieItem.allergy.code, Validators.required],
      note: allergieItem.note,
      type,
    });
    (this.allergiesForm.get(type) as FormArray).push(newAllergyForm);
  }

  get drugAllergyList(): FormArray {
    return this.allergiesForm.get('drug_classes') as FormArray;
  }

  get naturalAllergyList(): FormArray {
    return this.allergiesForm.get('natural_ingredients') as FormArray;
  }

  get chemicalAllergyList(): FormArray {
    return this.allergiesForm.get('chemical_ingredients') as FormArray;
  }

  remove(type, i) {
    (this.allergiesForm.get(type) as FormArray).removeAt(i);
  }

  saveToRequest(saveAllergies) {
    this.medicalHistoryService
      .saveRequestMedicalHistory$(this.requestUuid, 'allergies', saveAllergies)
      .pipe(take(1))
      .subscribe((request) => {
        if (request) {
          this.submittedStep.emit({
            step: 'allergies',
            values: saveAllergies,
          });
          this.alertService.snackbar('allergies_saved');
        }
      });
  }

  saveToProfile(saveAllergies) {
    this.medicalHistoryService
      .saveMedicalHistory$(this.userId, 'allergies', { user_id: this.userId, allergies: saveAllergies })
      .pipe(take(1))
      .subscribe(() => this.alertService.snackbar('allergies_saved'));
  }

  submit() {
    const saveAllergies = this.allergiesForm.value;
    // goodxweb's no_allergies toggle is inverted, however this makes more sense for a user
    // This should be a string and not a boolean
    saveAllergies.no_allergies = saveAllergies.no_allergies === 'true' ? 'false' : 'true';
    saveAllergies.warning = { urgent: saveAllergies.urgent, message: saveAllergies.warning };
    if (this.requestUuid) {
      this.saveToRequest(saveAllergies);
    } else {
      this.saveToProfile(saveAllergies);
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
